import { useMemo } from 'react';
import {
  SpreadsheetConfig,
  SpreadsheetConfigConstructorParams,
} from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { NAME_SHEET_FUND_KEY, RELATED_ENTITY_OWNER_VALUE } from 'pages/Waterfall/constants';
import { UseFirmTotalSheetConfigProps } from './useFirmTotalSheetConfig.props';
import { shouldUpdateFundInvestmentSheetNames } from './useFirmTotalSheetConfig.utils';
import firmInvestmentParser, { FirmInvestmentColumn } from '../FirmInvestmentParser/firmInvestmentParser';
import getFirmInvestmentRowConfig from '../FirmInvestmentRowConfig/getFirmInvestmentRowConfig';

const useFirmTotalSheetConfig = ({
  securityCount,
  investmentsInPortfolioSheets,
  allInvestmentsSheets,
  weightedShareValues,
  isSelectedPortfolioPrimaryCapTable,
  fundInvestmentsSheetsNamesRef,
}: UseFirmTotalSheetConfigProps) => {
  const relatedEntitiesOwnShares = useMemo(
    () => Boolean((weightedShareValues ?? []).filter(({ owner }) => owner === RELATED_ENTITY_OWNER_VALUE).length),
    [weightedShareValues]
  );

  const investmentsInPortfolioSheetNames = useMemo(
    () => investmentsInPortfolioSheets.map(sheet => sheet.name),
    [investmentsInPortfolioSheets]
  );

  // An unsettled parent result is an empty workbook identity; registered child configs
  // may still belong to the previous request until their lifecycle effect clears them.
  const incomingFundSheetNames =
    weightedShareValues === undefined
      ? []
      : allInvestmentsSheets
        .map(sheet => sheet.name)
        .filter(name => name.endsWith(`_${NAME_SHEET_FUND_KEY}`));
  if (shouldUpdateFundInvestmentSheetNames(fundInvestmentsSheetsNamesRef.current, incomingFundSheetNames)) {
    // this ref is shared with InputSheet to build the expression for Firm Total Exit Proceeds
    // eslint-disable-next-line no-param-reassign
    fundInvestmentsSheetsNamesRef.current = incomingFundSheetNames;
  }

  const fundInvestmentsSheetNames = fundInvestmentsSheetsNamesRef.current;

  const spreadsheet = useMemo(() => {
    const shouldNotShowSheet =
      (fundInvestmentsSheetNames.length <= 1 && !relatedEntitiesOwnShares) ||
      (fundInvestmentsSheetNames.length <= 1 && !isSelectedPortfolioPrimaryCapTable);
    if (shouldNotShowSheet) {
      return undefined;
    }
    type FirmInvestmentConfigParams = SpreadsheetConfigConstructorParams<
      object,
      FirmInvestmentColumn,
      object,
      object
    >;
    const configParams = {
      columns: [{ label: 'Share Count' }, { label: 'Value' }],
      rowConfig: getFirmInvestmentRowConfig({
        securityCount,
        fundInvestmentsSheetNames,
        investmentsInPortfolioSheetNames,
      }),
      parser: firmInvestmentParser,
      reverseParser: undefined,
      showTitlesColumn: false,
      name: 'FirmInvestmentSheet',
    } as unknown as FirmInvestmentConfigParams;

    return new SpreadsheetConfig<object, FirmInvestmentColumn, object>(configParams);
  }, [
    securityCount,
    relatedEntitiesOwnShares,
    fundInvestmentsSheetNames,
    isSelectedPortfolioPrimaryCapTable,
    investmentsInPortfolioSheetNames,
  ]);
  return {
    spreadsheet,
  };
};

export default useFirmTotalSheetConfig;
